import React from 'react';
import { createRoot } from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { scrubTokensDeep } from '@rr-crm/shared';
import * as Sentry from '@sentry/react';

import { App } from './App';
import { LoginHero } from './auth/LoginHero';
import { msalConfig } from './auth/msalConfig';
import { setMsalInstance } from './api/client';
import { createQueryClient } from './api/queryClient';
import { OfflineBanner, RrUpdatePrompt } from '@rr-crm/shared/components';
// T4_6-003 — Import the theme-switch hook for its module-load side effect.
// The hook reads localStorage `rr.theme` and applies the persisted
// `[data-theme]` attribute to `<html>` before first paint, so the user's
// chosen mode is in effect from the initial render rather than flickering
// from un-themed to themed once a consumer mounts. Even without a visible
// toggle in the CRM yet (T4_6-004 nav adds it), this import keeps theme
// persistence working across sessions.
import '@rr-crm/shared/hooks';
import { registerServiceWorker } from './sw/registerSW';

import './index.css';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate: 0.1,
    // QA 2026-10-01 item 7: the deploy target ('staging' or 'prod', set by the
    // frontend deploy workflow, the same names the backend uses). Vite's MODE
    // is 'production' for every deployed build, so staging errors read as
    // production ones.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    // T1-011: replay at 10% normal / 100% on error per spec.
    integrations: [Sentry.replayIntegration()],
    // Customer link tokens (/by-token/<token>, /intake/<token>,
    // /proposal/accept/<token>) are credentials; mask them in every event,
    // transaction and breadcrumb before it leaves the browser.
    beforeSend: (event) => scrubTokensDeep(event),
    beforeSendTransaction: (event) => scrubTokensDeep(event),
    beforeBreadcrumb: (crumb) => scrubTokensDeep(crumb),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // T1-011: release tag derives from APP_COMMIT_SHA when CI injects it
    // (defaults to MODE for dev). The CI source-maps upload step (deferred
    // to a separate Phase 1+ ticket) uses the same release identifier.
    release: import.meta.env.VITE_APP_COMMIT_SHA
      ? `${import.meta.env.MODE}-${import.meta.env.VITE_APP_COMMIT_SHA}`
      : import.meta.env.MODE,
  });
}

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

/**
 * RIG-414 — strip the OAuth response fragment from the address bar.
 *
 * A completed sign-in landed on
 * `https://crm.therightrole.com/#code=1.AWEB…&client_info=…&state=…&session_state=…`
 * and nothing removed it. The fragment is never sent to the server, so this is
 * not a wire exposure, but it persists client-side in ways that matter: it
 * stays in browser history (syncing across every device on that profile), it
 * is what a user copies when they share "the CRM link" right after signing in,
 * it is on screen during any demo that starts with a login, and any extension
 * with tab-read access can see it.
 *
 * Authorization codes are short-lived, single-use and PKCE-bound, so this is
 * not urgent on its own. It is a finding an enterprise security questionnaire
 * will raise, and cheap to close now.
 *
 * Done HERE, on the auth callback path, rather than in a component effect that
 * might not run on every entry. `handleRedirectPromise` is idempotent —
 * MsalProvider calls it too and gets the cached result — but awaiting it first
 * guarantees the token exchange has finished before the fragment carrying the
 * code is discarded.
 *
 * `replaceState`, not `pushState`, so the dirty URL is not reachable with the
 * Back button.
 */
const hadAuthFragment = /[#&](code|error)=/.test(globalThis.location.hash);
try {
  await msalInstance.handleRedirectPromise();
} catch {
  // A failed redirect leaves MsalProvider to surface the unauthenticated
  // state; the fragment is still cleaned below so a dead code does not linger.
}
if (hadAuthFragment) {
  // `navigateToLoginRequestUrl: true` already returns the user to the deep
  // link they asked for before signing in, so only the bare landing case is
  // rewritten: `/` is the dashboard route, and saying so in the address bar
  // is clearer than a bare origin.
  const landing =
    globalThis.location.pathname === '/' ? '/dashboard' : globalThis.location.pathname;
  globalThis.history.replaceState({}, document.title, landing + globalThis.location.search);
}

setMsalInstance(msalInstance);

// QA 2026-10-01 F-33: no retry on a 4xx (a missing account shows "not found"
// at once), a small retry for network and 5xx failures.
const queryClient = createQueryClient();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root not found');

// ── DEV-ONLY offline auth bypass (T-AI-RAG dev harness) ──────────────────────
// With `vite dev` + VITE_DEV_NO_AUTH=true, render <App/> directly instead of
// gating on a signed-in MSAL account. Pairs with the backend DEV_FAKE_STAFF shim
// (apiFetch sends no Authorization header → backend authenticates the fake staff
// user). BUILD-SAFE: `import.meta.env.DEV` is false in any production build, so
// this is dead-code-eliminated and can never ship. MsalProvider stays mounted so
// useMsal() in <App/> keeps working (accounts is just empty).
const DEV_NO_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_NO_AUTH === 'true';
if (DEV_NO_AUTH) {
  // eslint-disable-next-line no-console
  console.warn(
    '[DEV] VITE_DEV_NO_AUTH active — MSAL login gate bypassed (development only). ' +
      'Backend must run with DEV_FAKE_STAFF set.',
  );
}

createRoot(rootElement).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
      <OfflineBanner />
      <RrUpdatePrompt />
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            {/*
              LOGIN-HERO — the CRM gates the entire app on a signed-in MSAL
              account. AuthenticatedTemplate renders <App /> once an account
              exists; UnauthenticatedTemplate renders the branded Team Access
              hero (LoginHero) before sign-in.

              This replaces the prior MsalAuthenticationTemplate
              (interactionType=Redirect) auto-redirect (T1-055E Round 2
              Finding A). The AUTH FLOW IS UNCHANGED — the hero's CTA calls
              instance.loginRedirect({ scopes: apiScopes }), the identical
              redirect + identical scopes the template fired automatically.
              The only change is presentation: the user now sees a branded
              pre-login surface and clicks "Sign in with Microsoft" instead
              of bouncing straight to login.microsoftonline.com on load.

              apiScopes (the backend API audience scope consumed by
              acquireTokenSilent in apps/crm/src/api/client.ts) is passed by
              LoginHero on the loginRedirect call.
            */}
            {DEV_NO_AUTH ? (
              <App />
            ) : (
              <>
                <AuthenticatedTemplate>
                  <App />
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                  <LoginHero />
                </UnauthenticatedTemplate>
              </>
            )}
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

registerServiceWorker();
